import React, { useEffect, useState } from "react";
import Footer from "../../components/Footer/Footer";
import Navbar from "../../components/Navbar/Navbar";
import Loading from "../../components/Loading/Loading";
import { toast } from "react-toastify";
import axios from "axios";
import styles from "./AgencyView.module.css";
import AgencyListCard from "../../components/AgencyListCard/AgencyListCard";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import EmployeeCard from "../../components/AgencyListCard/EmployeeListCard/EmployeeCard";
import Listing from "../../components/Listing/Listing";
import { listingStates } from "../../util/flStates";

const defaultLogo = "https://via.placeholder.com/150";

export default function AgencyView() {
  const [isLoading, setIsLoading] = useState(true);
  const [agencies, setAgencies] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [listings, setListings] = useState([]);
  const [isEmployeesView, setIsEmployeesView] = useState(true);

  const showErrorMessage = (text) => {
    toast.error(text, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      theme: "colored",
    });
  };

  const handleAgencyCardClick = async (agency) => {
    setSelectedAgency(agency);
    setIsModalOpen(true);
    fetchAgencyListings(agency.id, listingStates.sell);

    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/agency/${agency.id}/professionals`
      );
      if (response.status === 200 && response.data.success) {
        setEmployees(response.data.professionals || []);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error("Error fetching professionals:", error);
      showErrorMessage("Error fetching professionals. Please try again!");
      setEmployees([]);
    }
  };

  const fetchAgencyListings = async (agencyId, listingType) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/agency/listings`,
        {
          params: { agencyId, listingType },
        }
      );
      if (response.data.success) {
        setListings(response.data.listings || []);
      } else {
        setListings([]);
      }
    } catch (error) {
      console.error("Error fetching listings:", error.message);
      setListings([]);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAgency(null);
    setEmployees([]);
    setListings([]);
  };

  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/agency/list`
        );
        if (response.status === 200 && response.data.success) {
          setAgencies(response.data.agencies);
        } else {
          showErrorMessage("Error fetching agencies. Please try again!");
        }
      } catch (error) {
        console.error("Error fetching agencies:", error);
        showErrorMessage("Error fetching agencies. Please try again!");
      } finally {
        setIsLoading(false);
      }
    };
    fetchAgencies();
  }, []);

  return (
    <>
      <Navbar />
      <div className={styles.containerWrap}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Loading />
          </div>
        ) : (
          <div className={styles.container}>
            <h2 className={styles.chartTitle}>Agency Organizational Chart</h2>
            <div className={styles.listContainer}>
              {agencies.length > 0 ? (
                agencies.map((agency) => (
                  <AgencyListCard
                    key={agency.id}
                    agencyData={agency}
                    openAgency={() => handleAgencyCardClick(agency)}
                  />
                ))
              ) : (
                <div>There are no agencies registered!</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ style: { padding: "20px", maxWidth: "1000px" } }}
      >
        <DialogContent>
          <div className={styles.modalHeader}>
            <div className={styles.toggleButtonsContainer}>
              <Button
                variant={isEmployeesView ? "contained" : "outlined"}
                onClick={() => setIsEmployeesView(true)}
                className={
                  isEmployeesView ? styles.activeToggle : styles.inactiveToggle
                }
              >
                Employees
              </Button>
              <Button
                variant={!isEmployeesView ? "contained" : "outlined"}
                onClick={() => setIsEmployeesView(false)}
                className={
                  !isEmployeesView ? styles.activeToggle : styles.inactiveToggle
                }
              >
                Listings
              </Button>
            </div>
          </div>

          <h3 className={styles.modalTitle}>Organization Structure</h3>

          {selectedAgency && (
            <div className={styles.parentAgencyContainer}>
              <AgencyListCard agencyData={selectedAgency} />
              <div className={styles.verticalLine}></div>
            </div>
          )}

          {isEmployeesView ? (
            <div className={styles.employeeTree}>
              {employees.length > 0 ? (
                employees.map((employee, index) => (
                  <div
                    key={employee.id || index}
                    className={styles.employeeNode}
                  >
                    <EmployeeCard employee={employee} />
                    {index < employees.length - 1 && (
                      <div className={styles.connectorLine}></div>
                    )}
                  </div>
                ))
              ) : (
                <div>No employees found for this agency.</div>
              )}
            </div>
          ) : (
            <div className={styles.listingTree}>
              {listings.length > 0 ? (
                listings.map((listing, index) => (
                  <div key={listing.id || index} className={styles.listingNode}>
                    <Listing listing={listing} />
                    {index < listings.length - 1 && (
                      <div className={styles.connectorLine}></div>
                    )}
                  </div>
                ))
              ) : (
                <div>No listings found for this agency.</div>
              )}
            </div>
          )}
        </DialogContent>

        <Button
          onClick={handleCloseModal}
          color="primary"
          className={styles.closeButton}
        >
          Close
        </Button>
      </Dialog>

      <Footer />
    </>
  );
}
